import {Injectable} from '@angular/core';
import {catchError, map} from 'rxjs/operators';

import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {
  CommonPaginationResponse, CommonResponse,
  PaginateResponse, processNormal,
  processPagination,
} from '../common/response.model';
import {ErrorHandlingService} from '../common/error-handling.service';
import {Observable} from 'rxjs';
import {ConsultationsReport} from './models/consultations-report.model';
import {ExportReport} from './models/export-report.model';
import {DiagnosesReport} from './models/diagnoses-report.model';
import {InvoicesReport} from './models/invoices-report.model';
import {PatientTreatedReport} from './models/patient-treated-report.model';
import {
  PrescriptionInterconsultationAmountReport,
  PrescriptionMedicalStudiesAmountReport,
  PrescriptionMedicineAmountReport
} from './models/prescriptions-amount-report.model';
import {PrescriptionInterconsultation, PrescriptionMedicalStudy, PrescriptionMedicine} from '../prescriptions/prescription.model';
import {ReportsFilter} from './models/reports-filter.model';
import {DateCommonHelper} from '../../helpers/date-common.helper';

@Injectable()
export class ReportsService {

  constructor(private http: HttpClient, private errorHandling: ErrorHandlingService) {
  }

  getInvoicesReportData(for_panel: string, page, per_page, filters: ReportsFilter): Observable<PaginateResponse<InvoicesReport[]>> {

    let params = new HttpParams().append('data_for', for_panel)
      .append('paginate_per_page', per_page.toString()).append('page', page);

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonPaginationResponse<InvoicesReport>>(
      environment.apiUrl + 'reports/invoices',
      {
        params
      }
    ).pipe(
      map(resp => processPagination(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDownloadInvoicesReportData(for_panel: string, filters: ReportsFilter): Observable<ExportReport> {

    let params = new HttpParams().append('data_for', for_panel).append('export', '1');

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonResponse<ExportReport>>(
      environment.apiUrl + 'reports/invoices',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getConsultationsReportData(for_panel: string, page, per_page, filters: ReportsFilter):
    Observable<PaginateResponse<ConsultationsReport[]>> {

    let params = new HttpParams().append('data_for', for_panel).append('paginate_per_page', per_page.toString()).append('page', page);

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonPaginationResponse<ConsultationsReport>>(
      environment.apiUrl + 'reports/consultations',
      {
        params
      }
    ).pipe(
      map(resp => processPagination(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDownloadConsultationsReportData(for_panel: string, filters: ReportsFilter): Observable<ExportReport> {

    let params = new HttpParams().append('data_for', for_panel).append('export', '1');

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonResponse<ExportReport>>(
      environment.apiUrl + 'reports/consultations',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDiagnosesReportData(for_panel: string, filters: ReportsFilter): Observable<DiagnosesReport[]> {

    let params = new HttpParams().append('data_for', for_panel);

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonPaginationResponse<DiagnosesReport>>(
      environment.apiUrl + 'reports/diagnoses',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDownloadDiagnosesReportData(for_panel: string, filters: ReportsFilter): Observable<ExportReport> {

    let params = new HttpParams().append('data_for', for_panel).append('export', '1');

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonResponse<ExportReport>>(
      environment.apiUrl + 'reports/diagnoses',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getPatientsTreatedReportData(filters: ReportsFilter): Observable<PatientTreatedReport[]> {

    let params = new HttpParams();

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonPaginationResponse<PatientTreatedReport>>(
      environment.apiUrl + 'reports/patients-treated',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDownloadPatientsTreatedReportData(filters: ReportsFilter): Observable<ExportReport> {

    let params = new HttpParams().append('export', '1');

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonResponse<ExportReport>>(
      environment.apiUrl + 'reports/patients-treated',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }


  getPrescriptionMedicineAmountReportData(page, per_page, filters: ReportsFilter):
    Observable<PaginateResponse<PrescriptionMedicineAmountReport[]>> {

    let params = new HttpParams().append('paginate_per_page', per_page.toString()).append('page', page);

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonPaginationResponse<PrescriptionMedicineAmountReport>>(
      environment.apiUrl + 'reports/prescriptions-count/medicines',
      {
        params
      }
    ).pipe(
      map(resp => processPagination(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDownloadPrescriptionMedicineAmountReportData(filters: ReportsFilter): Observable<ExportReport> {

    let params = new HttpParams().append('export', '1');

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonResponse<ExportReport>>(
      environment.apiUrl + 'reports/prescriptions-count/medicines',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getPrescriptionInterconsultationAmountReportData(page, per_page, filters: ReportsFilter):
    Observable<PaginateResponse<PrescriptionInterconsultationAmountReport[]>> {

    let params = new HttpParams().append('paginate_per_page', per_page.toString()).append('page', page);

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonPaginationResponse<PrescriptionInterconsultationAmountReport>>(
      environment.apiUrl + 'reports/prescriptions-count/interconsultations',
      {
        params
      }
    ).pipe(
      map(resp => processPagination(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDownloadPrescriptionInterconsultationAmountReportData(filters: ReportsFilter): Observable<ExportReport> {

    let params = new HttpParams().append('export', '1');

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonResponse<ExportReport>>(
      environment.apiUrl + 'reports/prescriptions-count/interconsultations',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getPrescriptionMedicalStudiesAmountReportData(page, per_page, filters: ReportsFilter):
    Observable<PaginateResponse<PrescriptionMedicalStudiesAmountReport[]>> {

    let params = new HttpParams().append('paginate_per_page', per_page.toString()).append('page', page);

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonPaginationResponse<PrescriptionMedicalStudiesAmountReport>>(
      environment.apiUrl + 'reports/prescriptions-count/medical_studies',
      {
        params
      }
    ).pipe(
      map(resp => processPagination(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDownloadPrescriptionMedicalStudiesAmountReportData(filters: ReportsFilter): Observable<ExportReport> {

    let params = new HttpParams().append('export', '1');

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonResponse<ExportReport>>(
      environment.apiUrl + 'reports/prescriptions-count/medical_studies',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }


  getPrescriptionMedicineReportData(page, per_page, filters: ReportsFilter):
    Observable<PaginateResponse<PrescriptionMedicine[]>> {

    let params = new HttpParams().append('paginate_per_page', per_page.toString()).append('page', page);

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonPaginationResponse<PrescriptionMedicine>>(
      environment.apiUrl + 'reports/prescriptions/medicines',
      {
        params
      }
    ).pipe(
      map(resp => processPagination(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDownloadPrescriptionMedicineReportData(filters: ReportsFilter): Observable<ExportReport> {

    let params = new HttpParams().append('export', '1');

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonResponse<ExportReport>>(
      environment.apiUrl + 'reports/prescriptions/medicines',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getPrescriptionInterconsultationReportData(page, per_page, filters: ReportsFilter):
    Observable<PaginateResponse<PrescriptionInterconsultation[]>> {

    let params = new HttpParams().append('paginate_per_page', per_page.toString()).append('page', page);

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonPaginationResponse<PrescriptionInterconsultation>>(
      environment.apiUrl + 'reports/prescriptions/interconsultations',
      {
        params
      }
    ).pipe(
      map(resp => processPagination(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDownloadPrescriptionInterconsultationReportData(filters: ReportsFilter): Observable<ExportReport> {

    let params = new HttpParams().append('export', '1');

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonResponse<ExportReport>>(
      environment.apiUrl + 'reports/prescriptions/interconsultations',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getPrescriptionMedicalStudiesReportData(page, per_page, filters: ReportsFilter):
    Observable<PaginateResponse<PrescriptionMedicalStudy[]>> {

    let params = new HttpParams().append('paginate_per_page', per_page.toString()).append('page', page);

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonPaginationResponse<PrescriptionMedicalStudy>>(
      environment.apiUrl + 'reports/prescriptions/medical_studies',
      {
        params
      }
    ).pipe(
      map(resp => processPagination(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  getDownloadPrescriptionMedicalStudiesReportData(filters: ReportsFilter): Observable<ExportReport> {

    let params = new HttpParams().append('export', '1');

    if (filters !== undefined) {
      for (const filterParam of this.processFilter(filters)) {
        params = params.append(filterParam.title, filterParam.value);
      }
    }

    return this.http.get<CommonResponse<ExportReport>>(
      environment.apiUrl + 'reports/prescriptions/medical_studies',
      {
        params
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  private processFilter(filters: ReportsFilter): { title: string, value: string }[] {

    const response = [];

    // Se procesan los rangos de fecha
    if (filters.dateRange !== undefined) {
      response.push({
        title: 'date_from',
        value: DateCommonHelper.ngbDateStructToString(filters.dateRange.from)
      });

      response.push({
        title: 'date_to',
        value: DateCommonHelper.ngbDateStructToString(filters.dateRange.to)
      });
    }

    return response;
  }


}
